import React, { FC, useEffect, useMemo } from 'react';

import { Table, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import i18n from '@/content';
import { PolicyTag } from '@/components/FilesTable/Columns';
import { getLinkData, CopyLinkButton } from '@/components/Common/CopyLink';
import { useStores } from '@/components/hooks';
import type { Recipient } from '@/types/types';
import { FolderToDisplay, UploadedItem } from '@/stores';
import { ApplyRulesResponse } from '@/api/requests';
import FileIcon from '@/components/Common/Icons/FileIcon';
import './index.scss';

const { Column } = Table;

const nameSpace = 'uploadFiles.successTable';

interface SharedFile {
    fid: string;
    filename: string;
    isFolder?: boolean;
    children?: SharedFile[];
    policy?: { id: string; title: string; isMfa?: boolean };
}

interface SharedFilesTableProps {
    rulesResults?: ApplyRulesResponse;
    sharedFiles?: SharedFile[];
}

interface SharedTableRow {
    key: string;
    filename: string;
    fid: string;
    isFolder?: boolean;
    isChildFile?: boolean;
    childCount?: number;
    policy?: { id: string; title: string; isMfa?: boolean };
}

const SharedFilesTable: FC<SharedFilesTableProps> = observer(({ rulesResults = [], sharedFiles }) => {
    const { t } = useTranslation();
    const { sharedUsersStore, uploadFilesStore } = useStores();
    const { recipientsList, shareLinksToJS } = sharedUsersStore;
    const { successfullyUploadedItems, modifiedFoldedFiles } = uploadFilesStore;

    const hasSharedFiles = sharedFiles && sharedFiles.length > 0;

    useEffect(() => {
        if (hasSharedFiles) return;
        sharedUsersStore.startLinksPreloading({
            filesIds: uploadFilesStore.uploadedFileIds,
            mode: 'personal',
        });
        return () => {
            sharedUsersStore.cancelLinksPreloading();
        };
    }, []);

    // Build a map of file_id -> blocked recipient emails
    const blockedRecipientsPerFile = useMemo(() => {
        const map = new Map<string, string[]>();
        const blockRules = rulesResults.filter((r) => r.action === 'block');

        blockRules.forEach((rule) => {
            if (!map.has(rule.file_id)) {
                map.set(rule.file_id, []);
            }
            const list = map.get(rule.file_id);
            rule.recipients?.forEach((email) => {
                if (!list.includes(email)) {
                    list.push(email);
                }
            });
        });

        return map;
    }, [rulesResults]);

    // Set of file IDs that had policies assigned by rules
    const fileIdsWithRulePolicy = useMemo(() => {
        const set = new Set<string>();
        rulesResults
            .filter((r) => r.action === 'policy' && r.policy_id)
            .forEach((r) => set.add(r.file_id));
        return set;
    }, [rulesResults]);

    // Recipients who are blocked from every file — exclude them from "Shared with"
    const sharedWithRecipients = useMemo(() => {
        const allItems = hasSharedFiles ? sharedFiles : successfullyUploadedItems;
        const fileIds = allItems
            .map((f) => f.fid)
            .filter(Boolean) as string[];
        const entirelyBlockedIdentities = new Set<string>();
        recipientsList.forEach((r) => {
            const identity = r.type === 'user' ? r.email.toLowerCase() : r.id;
            if (fileIds.length === 0) return;
            const isBlockedFromAll = fileIds.every((fid) => {
                const list = blockedRecipientsPerFile.get(fid) ?? [];
                if (r.type === 'user') {
                    return list.some((blocked) => blocked.toLowerCase() === identity);
                }
                return list.includes(identity);
            });
            if (isBlockedFromAll) entirelyBlockedIdentities.add(identity);
        });
        return recipientsList.filter(
            (r) => !entirelyBlockedIdentities.has(r.type === 'user' ? r.email.toLowerCase() : r.id),
        );
    }, [recipientsList, successfullyUploadedItems, blockedRecipientsPerFile]);

    // Files that are blocked for all recipients — exclude them from "Shared items"
    const sharedItems = useMemo(() => {
        const allItems = hasSharedFiles ? sharedFiles : successfullyUploadedItems;
        const recipientCount = recipientsList.length;
        return allItems.filter((item) => {
            if (!item.fid) return true;
            const blockedList = blockedRecipientsPerFile.get(item.fid) ?? [];
            if (recipientCount === 0) return true;
            const blockedRecipientCount = recipientsList.filter((r) => {
                const identity = r.type === 'user' ? r.email.toLowerCase() : r.id;
                if (r.type === 'user') return blockedList.some((b) => b.toLowerCase() === identity);
                return blockedList.includes(identity);
            }).length;
            return blockedRecipientCount !== recipientCount;
        });
    }, [recipientsList, successfullyUploadedItems, sharedFiles, hasSharedFiles, blockedRecipientsPerFile]);

    // Expand folders into folder row + child file rows (from sharedItems so entirely-blocked files are excluded)
    const tableDataSource: SharedTableRow[] = useMemo(() => {
        const rows: SharedTableRow[] = [];

        sharedItems.forEach((item) => {
            const uploadedItem = item as UploadedItem;
            const sharedFile = item as SharedFile;
            const isFolder = uploadedItem.isFolder || sharedFile.isFolder;

            const childCount = isFolder
                ? (hasSharedFiles
                    ? sharedFile.children?.length
                    : modifiedFoldedFiles.find(
                        (f): f is FolderToDisplay => f.isFolder && (f as FolderToDisplay).uid === uploadedItem.uid,
                    )?.foldedFiles?.filter((f) => f.status === 'done').length)
                : undefined;

            rows.push({
                key: item.fid || (uploadedItem.uid ?? item.fid),
                filename: item.filename,
                fid: item.fid,
                isFolder,
                childCount: childCount || 0,
                policy: item.policy,
            });

            if (isFolder && hasSharedFiles && sharedFile.children?.length) {
                sharedFile.children.forEach((child) => {
                    rows.push({
                        key: child.fid,
                        filename: child.filename,
                        fid: child.fid,
                        isChildFile: true,
                        policy: child.policy || item.policy,
                    });
                });
            }

            if (uploadedItem.isFolder && !hasSharedFiles) {
                const folderDisplay = modifiedFoldedFiles.find(
                    (f): f is FolderToDisplay => f.isFolder && (f as FolderToDisplay).uid === uploadedItem.uid,
                );

                if (folderDisplay?.foldedFiles) {
                    folderDisplay.foldedFiles
                        .filter((f) => f.status === 'done')
                        .forEach((childFile) => {
                            rows.push({
                                key: childFile.fid || childFile.uid,
                                filename: childFile.filename || childFile.name,
                                fid: childFile.fid,
                                isChildFile: true,
                                policy: fileIdsWithRulePolicy.has(childFile.fid)
                                    ? childFile.policy
                                    : (item.policy || childFile.policy),
                            });
                        });
                }
            }
        });

        return rows;
    }, [sharedItems, modifiedFoldedFiles, hasSharedFiles]);

    return (
        <div className="shared-files-table">
            {/* Shared with table */}
            <Table<Recipient>
                pagination={false}
                dataSource={sharedWithRecipients}
                className="table-styles recipients-table"
                rowKey="id"
                showHeader
            >
                <Column
                    title={i18n.t(`${nameSpace}.sharedWith`)}
                    dataIndex="email"
                    render={(email: string) => (
                        <div className="recipient-cell" title={email}>
                            {email}
                        </div>
                    )}
                />
                <Column
                    title={i18n.t(`${nameSpace}.role`)}
                    dataIndex="role"
                    width="120px"
                    render={(role: string) => (
                        <span className="role-cell">
                            {i18n.t(`sharingBlock.recipientRolesSelect.${role}.title`)}
                        </span>
                    )}
                />
                <Column
                    title={i18n.t(`${nameSpace}.copyLink`)}
                    width="76px"
                    align="center"
                    render={(_: unknown, recipient: Recipient) => {
                        const { email, id, type } = recipient;
                        const identity = type === 'user' ? email : id;
                        const { link, isLinkLoading } = getLinkData(
                            shareLinksToJS,
                            identity,
                        );

                        return (
                            <CopyLinkButton
                                link={link}
                                isLinkLoading={isLinkLoading}
                                linkKey={identity}
                                className="copy-button"
                            />
                        );
                    }}
                />
            </Table>

            {/* Shared items table */}
            <Table<SharedTableRow>
                pagination={false}
                dataSource={tableDataSource}
                className="table-styles items-table"
                rowKey="key"
                showHeader
            >
                <Column
                    title={i18n.t(`${nameSpace}.sharedFiles`, { filesNumber: tableDataSource.filter((r) => !r.isChildFile).length })}
                    render={(_: unknown, item: SharedTableRow) => {
                        const blockedEmails = blockedRecipientsPerFile.get(item.fid);
                        const hasBlocked = blockedEmails && blockedEmails.length > 0;

                        return (
                            <div
                                className="filename-cell"
                                title={item.filename}
                                style={item.isChildFile ? { paddingLeft: 24 } : undefined}
                            >
                                {hasBlocked && (
                                    <Tooltip
                                        placement="top"
                                        color="#091F56"
                                        title={(
                                            <div>
                                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
                                                    {t('uploadFiles.policyStep.isBlockedFor')}
                                                </div>
                                                {blockedEmails.map((email) => (
                                                    <div key={email} style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.6 }}>
                                                        {email}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    >
                                        <WarningOutlined className="blocked-warning-icon" />
                                    </Tooltip>
                                )}
                                <span className="icon-wrapper">
                                    <FileIcon
                                        filename={item.filename}
                                        isFolder={!!item.isFolder}
                                    />
                                </span>
                                <span>{item.filename}</span>
                                {item.isFolder && item.childCount != null && item.childCount > 0 && (
                                    <span className="folder-child-count">
                                        {' | '}
                                        {item.childCount}
                                        {' '}
                                        {item.childCount === 1 ? 'file' : 'files'}
                                    </span>
                                )}
                            </div>
                        );
                    }}
                />
                <Column
                    title={i18n.t(`${nameSpace}.policy`)}
                    width="196px"
                    render={(_: unknown, item: SharedTableRow) => {
                        if (!item.policy?.title) return null;

                        const isRuleAssigned = fileIdsWithRulePolicy.has(item.fid);

                        return (
                            <PolicyTag
                                title={item.policy.title}
                                isLocked={isRuleAssigned}
                                showTooltip
                            />
                        );
                    }}
                />
            </Table>
        </div>
    );
});

export default SharedFilesTable;
